import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError, isSignupPasswordRejectedError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  checkSession,
  createUserWithIdp,
  loginToServer,
  logoutFromServer,
  signupToServer,
} from '../util/api';
import { AUTH_STATUS } from '../util/authConstants';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Initial State ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,
  signupConfirmationRequired: false,
  signupPendingEmail: null,
  signupPendingFirstName: null,
  emailJustVerified: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

// ================ Async Thunks ================ //

const authInfoThunk = createAsyncThunk('auth/authInfo', (_, thunkAPI) => {
  const { extra: sdk } = thunkAPI;
  return sdk.authInfo().catch(e => {
    // Cookie read; should not normally fail. Active session validity is
    // enforced lazily via the auth-401 middleware (any API call returning
    // 401/403 cascades a full logout) and the drift watcher (Supabase
    // SIGNED_OUT events). No upfront round-trip.
    log.error(e, 'auth-info-failed');
    return null;
  });
});

const checkSessionThunk = createAsyncThunk('auth/checkSupabase', async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  if (!state.auth?.isAuthenticated) return { ok: true, skipped: true };
  try {
    await checkSession();
    return { ok: true };
  } catch (e) {
    return thunkAPI.rejectWithValue(storableError(e));
  }
});

const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ username, password }, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      const result = await loginToServer({ email: username, password });
      if (result?.status === AUTH_STATUS.CONFIRMATION_REQUIRED) {
        return { username, confirmationRequired: true, email: result.email || username };
      }
      await dispatch(fetchCurrentUser({ afterLogin: true }));
      return { username };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'loginInProgress')) {
        return false;
      }
    },
  }
);

const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      await logoutFromServer();
      dispatch(clearCurrentUser());
      log.clearUserId();
      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'logoutInProgress')) {
        return false;
      }
    },
  }
);

const signupThunk = createAsyncThunk(
  'auth/signup',
  async (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      const result = await signupToServer({
        email: params.email,
        password: params.password,
        firstName: params.firstName,
        lastName: params.lastName,
        ...(params.displayName ? { displayName: params.displayName } : {}),
        marketingConsent: params.marketingConsent === true,
        publicData: params.publicData || {},
        privateData: params.privateData || {},
        protectedData: params.protectedData || {},
      });

      if (result?.status === AUTH_STATUS.CONFIRMATION_REQUIRED) {
        return { ...params, confirmationRequired: true };
      }

      await dispatch(fetchCurrentUser({ afterLogin: true }));
      return params;
    } catch (e) {
      // A rejected password is the member being asked to pick another one, not
      // a fault worth an issue.
      if (!isSignupPasswordRejectedError(e)) {
        log.error(e, 'signup-failed', {
          email: params.email,
          firstName: params.firstName,
          lastName: params.lastName,
        });
      }
      return rejectWithValue(storableError(e));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'signupInProgress')) {
        return false;
      }
    },
  }
);

const signupWithIdpThunk = createAsyncThunk(
  'auth/signupWithIdp',
  (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    return createUserWithIdp(params)
      .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
      .then(() => params)
      .catch(e => {
        log.error(e, 'create-user-with-idp-failed', { params });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'confirmInProgress')) {
        return false;
      }
    },
  }
);

// ================ Slice ================ //

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setEmailJustVerified: (state, action) => {
      state.emailJustVerified = !!action.payload;
    },
    // Clears the pending confirm state so the "confirm your email" screen closes.
    dismissSignupConfirmation: state => {
      state.signupConfirmationRequired = false;
      state.signupPendingEmail = null;
      state.signupPendingFirstName = null;
    },
  },
  extraReducers: builder => {
    // Auth Info
    builder.addCase(authInfoThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      state.authInfoLoaded = true;
      state.isAuthenticated = authenticated(payload);
      state.isLoggedInAs = loggedInAs(payload);
      state.authScopes = payload?.scopes || [];
    });

    // Login
    builder
      .addCase(loginThunk.pending, state => {
        state.loginInProgress = true;
        state.loginError = null;
        state.logoutError = null;
        state.signupError = null;
        state.signupConfirmationRequired = false;
        state.signupPendingEmail = null;
        state.signupPendingFirstName = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loginInProgress = false;
        if (action.payload?.confirmationRequired) {
          state.signupConfirmationRequired = true;
          state.signupPendingEmail = action.payload?.email || null;
        } else {
          state.isAuthenticated = true;
          state.emailJustVerified = false;
        }
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loginInProgress = false;
        state.loginError = action.payload;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, state => {
        state.logoutInProgress = true;
        state.loginError = null;
        state.logoutError = null;
      })
      .addCase(logoutThunk.fulfilled, state => {
        state.logoutInProgress = false;
        state.isAuthenticated = false;
        state.isLoggedInAs = false;
        state.authScopes = [];
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.logoutInProgress = false;
        state.logoutError = action.payload;
      });

    // Signup
    builder
      .addCase(signupThunk.pending, state => {
        state.signupInProgress = true;
        state.loginError = null;
        state.signupError = null;
        state.signupConfirmationRequired = false;
        state.signupPendingEmail = null;
        state.signupPendingFirstName = null;
      })
      .addCase(signupThunk.fulfilled, (state, action) => {
        state.signupInProgress = false;
        if (action.payload?.confirmationRequired) {
          state.signupConfirmationRequired = true;
          state.signupPendingEmail = action.payload?.email || null;
          state.signupPendingFirstName = action.payload?.firstName || null;
        } else {
          state.isAuthenticated = true;
        }
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.signupInProgress = false;
        state.signupError = action.payload;
      });

    // Signup with IDP (Confirm)
    builder
      .addCase(signupWithIdpThunk.pending, state => {
        state.confirmInProgress = true;
        state.loginError = null;
        state.confirmError = null;
      })
      .addCase(signupWithIdpThunk.fulfilled, state => {
        state.confirmInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(signupWithIdpThunk.rejected, (state, action) => {
        state.confirmInProgress = false;
        state.confirmError = action.payload;
      });
  },
});

export { logoutThunk };
export const { setEmailJustVerified, dismissSignupConfirmation } = authSlice.actions;

export default authSlice.reducer;

// ================ Selectors ================ //

export const authenticationInProgress = (state, nextInProgress = 'any') => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  const anyInProgress =
    loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
  return nextInProgress === 'loginInProgress'
    ? loginInProgress || logoutInProgress || confirmInProgress
    : anyInProgress;
};

// ================ Thunk Wrappers ================ //
// These maintain the same API as the original thunks

export const login = (username, password) => dispatch => {
  return dispatch(loginThunk({ username, password }));
};

export const logout = () => dispatch => {
  return dispatch(logoutThunk());
};

export const signup = params => dispatch => dispatch(signupThunk(params));

export const signupWithIdp = params => dispatch => dispatch(signupWithIdpThunk(params));

export const authInfo = () => dispatch => {
  return dispatch(authInfoThunk());
};

export const checkSupabase = () => dispatch => dispatch(checkSessionThunk());
